<template>
  <section class="analytics-product-table">
    <div class="sticky-header">
      <div class="list-header">
        <div class="ui-search-wrap">
          <Search @onenter="onSearch" :disabled="is_loading" placeholder_text="Search by advertiser" />
        </div>
        <div class="list-btn-box">
          <Dropdown
            :items="sort_items"
            :direction_type="'right'"
            :icon="'filter'"
            :other_text="'Sort: '"
            :default_value="sort_index"
            @update-dropdown="onSortChange"
          />
          <Dropdown
            :items="per_page_items"
            :direction_type="'right'"
            :no_icon="true"
            :other_text="''"
            @update-dropdown="onPerPageChange"
          />
          <Pagenavi
            :disabled="last_page === 1"
            :page_current="current_page"
            :page_total="last_page"
            @goto-page="gotoPage"
          />
        </div>
      </div>
      <div class="short-link" v-if="statistic">
        <div class="report-box">
          <Loading v-if="is_loading" class="absolute" />
          <div class="report-item" v-if="!is_link_only">
            <SValue
              :is_wrap="true"
              :sum="statistic.product_views_sum"
              :value_text="'Product Views'"
              :show_sortlink="false"
            />
          </div>
          <div class="report-item">
            <SValue
              :chart_type_text="'click'"
              :is_wrap="true"
              :sum="statistic.all_click"
              :value_text="'Clicks'"
              :show_sortlink="sortlink_data === 'all' && !is_link_only"
              :sort_value="statistic.short_link_click_sum || 0"
              :store_value="statistic.click_sum || 0"
            />
          </div>
          <div class="report-item">
            <SValue
              :chart_type_text="'tran'"
              :is_wrap="true"
              :sum="statistic.all_sales"
              :value_text="'Sales'"
              :show_sortlink="sortlink_data === 'all' && !is_link_only"
              :sort_value="statistic.short_link_sales_sum || 0"
              :store_value="statistic.sales_sum || 0"
            />
          </div>
          <div class="report-item">
            <SValue
              :chart_type_text="'sales'"
              :is_wrap="true"
              :sum="statistic.all_sales_value"
              :value_text="'Sales Value'"
              :show_sortlink="sortlink_data === 'all' && !is_link_only"
              :sort_value="Math.ceil(statistic.short_link_sales_value_sum || 0)"
              :store_value="Math.ceil(statistic.sales_value_sum || 0)"
            />
          </div>
          <div class="report-item">
            <SValue
              :chart_type_text="'earnings'"
              :is_wrap="true"
              :sum="statistic.all_earnings"
              :value_text="'Earnings'"
              :show_sortlink="sortlink_data === 'all' && !is_link_only"
              :sort_value="Math.ceil(statistic.short_link_earnings_sum || 0)"
              :store_value="Math.ceil(statistic.earnings_sum || 0)"
            />
          </div>
        </div>
      </div>
    </div>

    <Loading v-if="is_loading && !statistic" class="static" />

    <Scrollbar class="data-list-scroll" :show_bar="true">
      <div v-if="data.length !== 0" class="product-table-view">
        <Loading v-if="is_loading" class="absolute" />
        <Table>
          <template #head>
            <tr>
              <th class="name-row">
                <span>Advertisers ({{ total }})</span>
              </th>
              <TableTh name="creator_count" title="Creators" :sort_value="sort" @sort="onColumnSort" content="Creators" />
              <TableTh name="total_products" title="Total Products" :sort_value="sort" @sort="onColumnSort" content="Total Products" />
              <TableTh v-if="!is_link_only" name="views" title="Product Views" :sort_value="sort" @sort="onColumnSort" content="Product Views" />
              <TableTh name="click" title="Product Clicks" :sort_value="sort" @sort="onColumnSort" content="Product Clicks" />
              <TableTh name="sales" title="Sales" :sort_value="sort" @sort="onColumnSort" content="Sales" />
              <TableTh name="sales_value" title="Sales Value" :sort_value="sort" @sort="onColumnSort" content="Sales Value" />
              <TableTh name="earnings" title="Earnings" :sort_value="sort" @sort="onColumnSort" content="Earnings" />
            </tr>
          </template>

          <template #body>
            <tr v-for="(item, index) in data" :key="index" @click.stop="openDetail(item)">
              <td class="name-row">{{ item.display_name || '---' }}</td>
              <td>
                <div class="creators-row">
                  <svg-icon name="users-2" />
                  {{ item.creator_count != null ? item.creator_count.toLocaleString('en-US') : '---' }}
                </div>
              </td>
              <td>{{ item.total_products != null ? item.total_products.toLocaleString('en-US') : '---' }}</td>
              <td v-if="!is_link_only">{{ item.views ? item.views.toLocaleString('en-US') : '---' }}</td>
              <td>{{ item.click ? item.click.toLocaleString('en-US') : '---' }}</td>
              <td>{{ item.sales ? item.sales.toLocaleString('en-US') : '---' }}</td>
              <td v-if="typeof item.sales_value === 'number'">
                ${{ item.sales_value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              <td v-else>---</td>
              <td v-if="typeof item.earnings === 'number'">
                ${{ item.earnings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              <td v-else>---</td>
            </tr>
          </template>

          <template #foot>
            <tr class="total-row" v-if="data_total">
              <td class="name-row">Total: {{ total }}</td>
              <td>{{ data_total.creator_count != null ? data_total.creator_count.toLocaleString('en-US') : '---' }}</td>
              <td>{{ data_total.total_products != null ? data_total.total_products.toLocaleString('en-US') : '---' }}</td>
              <td v-if="!is_link_only">{{ data_total.views ? data_total.views.toLocaleString('en-US') : '---' }}</td>
              <td>{{ data_total.click ? data_total.click.toLocaleString('en-US') : '---' }}</td>
              <td>{{ data_total.sales ? data_total.sales.toLocaleString('en-US') : '---' }}</td>
              <td v-if="typeof data_total.sales_value === 'number'">
                ${{ data_total.sales_value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              <td v-else>---</td>
              <td v-if="typeof data_total.earnings === 'number'">
                ${{ data_total.earnings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              <td v-else>---</td>
            </tr>
          </template>
        </Table>
      </div>

      <div v-if="!is_loading && data.length === 0">
        <NoData :image="require('@/assets/images/pc.png')" title="No data available" text="" />
      </div>
    </Scrollbar>
  </section>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import moment from 'moment';
import { dataSourceToApi, handleMaxMinFun } from '@/utils/SwitchFun.js';
import { per_page_items } from '@/composables/useAnalyticsProductList.js';
import Scrollbar from '@/components/ui/Scrollbar.vue';
import Search from '@/components/ui/Search.vue';
import Dropdown from '@/components/ui/Dropdown.vue';
import Pagenavi from '@/components/ui/Pagenavi.vue';
import Loading from '@/components/ui/Loading.vue';
import NoData from '@/components/creator/NoData.vue';
import Table from '@/components/ui/Table.vue';
import TableTh from '@/components/ui/TableTh.vue';
import SValue from '@/components/summary/SummaryValue.vue';

const props = defineProps({
  build_url: { type: Function, required: true },
  build_statistic_url: { type: Function, required: true },
});

const emit = defineEmits(['open-detail']);

const store = useStore();
const { proxy } = getCurrentInstance();
const $ajax = proxy.$ajax;
const EventBus = proxy.EventBus;

const is_loading = ref(true);
const data = ref([]);
const data_total = ref(null);
const statistic = ref(null);
const current_page = ref(1);
const last_page = ref(1);
const total = ref(0);
const keyword = ref('');
const sortlink_data = ref('all');
const per_page = ref(50);
const sort = reactive({ sort_key: 'sales_value', sort_val: 'desc' });
const sort_index = ref(0);

const start_date = ref(moment().add(-30, 'days').toDate());
const end_date = ref(moment().add(-1, 'days').toDate());

const is_link_only = computed(() => store.state.user_data?.merchant_account?.platform_type === 'linkonly');

const sort_items = [
  { text: 'Sort: Sales Value (Descending)', value: 'sales_value_desc' },
  { text: 'Sort: Sales Value (Ascending)', value: 'sales_value_asc' },
  { text: 'Sort: Sales (Descending)', value: 'sales_desc' },
  { text: 'Sort: Sales (Ascending)', value: 'sales_asc' },
  { text: 'Sort: Clicks (Descending)', value: 'click_desc' },
  { text: 'Sort: Clicks (Ascending)', value: 'click_asc' },
  { text: 'Sort: Views (Descending)', value: 'views_desc' },
  { text: 'Sort: Views (Ascending)', value: 'views_asc' },
  { text: 'Sort: Earnings (Descending)', value: 'earnings_desc' },
  { text: 'Sort: Earnings (Ascending)', value: 'earnings_asc' },
  { text: 'Sort: Creator (Descending)', value: 'creator_count_desc' },
  { text: 'Sort: Creator (Ascending)', value: 'creator_count_asc' },
];

const getBase = () => {
  const s = moment(start_date.value).format('YYYY-MM-DD');
  const e = moment(end_date.value).format('YYYY-MM-DD');
  return `start_date=${s}&end_date=${e}`;
};

const n = (v) => Number(v) || 0;

const plusData = (da) => {
  if (!da) return null;
  if (sortlink_data.value === 'all') return {
    ...da,
    all_click: n(da.click_sum) + n(da.short_link_click_sum),
    all_sales: n(da.sales_sum) + n(da.short_link_sales_sum),
    all_sales_value: Math.ceil(n(da.sales_value_sum)) + Math.ceil(n(da.short_link_sales_value_sum)),
    all_earnings: Math.ceil(n(da.earnings_sum)) + Math.ceil(n(da.short_link_earnings_sum)),
  };
  if (sortlink_data.value === 'shortlink') return {
    ...da,
    all_click: n(da.short_link_click_sum),
    all_sales: n(da.short_link_sales_sum),
    all_sales_value: Math.ceil(n(da.short_link_sales_value_sum)),
    all_earnings: Math.ceil(n(da.short_link_earnings_sum)),
  };
  return {
    ...da,
    all_click: n(da.click_sum),
    all_sales: n(da.sales_sum),
    all_sales_value: Math.ceil(n(da.sales_value_sum)),
    all_earnings: Math.ceil(n(da.earnings_sum)),
  };
};

const fetchStatistic = () => {
  const base = getBase();
  const sortlink = dataSourceToApi(sortlink_data.value);
  const url = props.build_statistic_url({
    base,
    sortlink,
    keyword: keyword.value,
    curated: 0,
  });

  $ajax(url, { method: 'get', withCredentials: true })
    .then((res) => {
      statistic.value = plusData(res.data.data || null);
    })
    .catch(() => {
      statistic.value = null;
    });
};

const fetch = () => {
  is_loading.value = true;
  const base = getBase();
  const sortlink = dataSourceToApi(sortlink_data.value);
  const url = props.build_url({
    base,
    sortlink,
    page: current_page.value,
    per_page: per_page.value,
    sort_key: sort.sort_key,
    sort_val: sort.sort_val,
    keyword: keyword.value,
  });

  $ajax(url, { method: 'get', withCredentials: true })
    .then((res) => {
      const d = res.data;
      data.value = d.data || [];
      data_total.value = d.totals || null;
      current_page.value = d.current_page || 1;
      last_page.value = d.last_page || 1;
      total.value = d.total || 0;
      is_loading.value = false;
    })
    .catch(() => {
      data.value = [];
      is_loading.value = false;
    });
};

const fetchAll = () => {
  fetchStatistic();
  fetch();
};

const onSearch = (val) => {
  keyword.value = val;
  current_page.value = 1;
  fetchAll();
};

const onSortChange = (item) => {
  const parts = item.value.split('_');
  sort.sort_val = parts.pop();
  sort.sort_key = parts.join('_');
  sort_index.value = sort_items.findIndex((s) => s.value === item.value);
  current_page.value = 1;
  fetch();
};

const onColumnSort = ({ sort_key, sort_val }) => {
  sort.sort_key = sort_key;
  sort.sort_val = sort_val;
  current_page.value = 1;
  fetch();
};

const onPerPageChange = (item) => {
  per_page.value = item.value;
  current_page.value = 1;
  fetch();
};

const gotoPage = (page) => {
  current_page.value = page;
  fetch();
};

const openDetail = (item) => {
  EventBus.emit('open-advertiser-detail', { id: item.advertiser_id, name: item.display_name });
  emit('open-detail', item);
};

const _onUpdateDate = (obj) => {
  start_date.value = obj.start_date;
  end_date.value = obj.end_date;
  current_page.value = 1;
  fetchAll();
};

const _onUpdateSortlink = (value) => {
  sortlink_data.value = value;
  current_page.value = 1;
  fetchAll();
};

const startFunction = () => {
  sortlink_data.value = store.state.save_sortlink_data || 'all';
  if (store.state.save_analytics_start_date && store.state.save_analytics_end_date) {
    start_date.value = store.state.save_analytics_start_date;
    end_date.value = store.state.save_analytics_end_date;
  }
  const checked = handleMaxMinFun(start_date.value, end_date.value, proxy);
  start_date.value = checked[0];
  end_date.value = checked[1];
  fetchAll();
};

onMounted(() => {
  EventBus.on('update-date', _onUpdateDate);
  EventBus.on('update-sortlink', _onUpdateSortlink);
  startFunction();
});

onBeforeUnmount(() => {
  EventBus.off('update-date', _onUpdateDate);
  EventBus.off('update-sortlink', _onUpdateSortlink);
});
</script>
